import { Box } from '@material-ui/core'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import SpeakerNotesOutlinedIcon from '@material-ui/icons/SpeakerNotesOutlined'
import { Fragment } from 'react'
import styled from 'styled-components'

import { isConsole } from '../../config/config'
import { timeAgo } from '../../helpers/formatters'
import { Handler } from '../../helpers/typeHelper'
import { useNoteAddedTags } from '../../hooks/query/useNoteAddedTags'
import { useNotes } from '../../hooks/query/useNotes'
import { ApiVersion } from '../../models'
import { isNote, NoteRecordType } from '../../models/note'
import { ProfileHeaderCard } from '../../pages/ProfilePage/Profile/ProfileHeader'
import { bgGray, blueGray, darkGray, lightGray1, lightGray2, primaryBlack } from '../../theme/colors'
import { Grid, InlineBlock, TextClamp } from '../Base/Base'
import { FlexBox } from '../Base/FlexBox'
import { InlineSpinner } from '../Spinner'
import { Text } from '../Text/Text'

const RECORD_TYPE_LABELS: Record<NoteRecordType, string> = {
  users: 'NOTES FOR',
  jobs: 'JOB NOTES',
  teams: 'FLEET NOTES',
  'console/employment-history': 'EMPLOYMENT HISTORY NOTES',
  'console/teams': 'FLEET NOTES',
}

interface NotesProps {
  count?: number
  onOpen?: Handler
  recordType: NoteRecordType
  recordId: string
  recordName?: string
  isTimeline?: boolean
  apiVersion?: ApiVersion
  isSlim?: boolean
}

export function NotesCard({
  recordType,
  recordId,
  recordName,
  isTimeline,
  onOpen,
  apiVersion,
  isSlim,
}: NotesProps) {
  const { data: notes, count, isLoading } = useNotes({ recordType, recordId, isTimeline, apiVersion })
  const enableTags = isConsole() && apiVersion === 'v3'
  const { tags, isLoading: isAddedTagsLoading } = useNoteAddedTags(
    { recordType, recordId },
    { enabled: enableTags }
  )
  const lastNote = notes?.find(isNote)

  function renderContent() {
    if (isLoading && !notes) {
      return <InlineSpinner mt={2} mb={2} />
    }

    if (isAddedTagsLoading && enableTags) {
      return <InlineSpinner mt={2} mb={2} />
    }

    if (tags?.length) {
      return (
        <>
          <Text variant="label3" fontColor={lightGray2} mt={1} mb={0.5}>
            Tags
          </Text>
          {tags.map((tag, i) => (
            <Fragment key={tag.value}>
              <InlineBlock key={tag.value}>
                <Text variant="header3" as="span" color={primaryBlack}>
                  {tag.label}
                  <Text
                    variant="header4"
                    as="span"
                    color={lightGray2}
                    styles="font-size: 13px; padding-left: 5px;"
                  >
                    {tag.count}
                  </Text>
                </Text>
                {i % 2 === 0 && tags[i + 1] && (
                  <Text variant="header3" as="span" color={lightGray2} ml={1} mr={1}>
                    •
                  </Text>
                )}
              </InlineBlock>
              {i % 2 === 1 ? <br /> : null}
            </Fragment>
          ))}
        </>
      )
    }

    return lastNote ? (
      <Text variant="label3">
        <TextClamp lines={2}>{lastNote.text} </TextClamp>
        <Text variant="label3" fontColor={lightGray1}>
          {timeAgo(new Date(lastNote.dateUpdated))}
        </Text>
      </Text>
    ) : (
      <Text variant="header3" fontColor={lightGray2} mt={1}>
        No notes added
      </Text>
    )
  }

  if (isSlim) {
    return (
      <SlimNotesCard
        className="slim-notes-card"
        isFullWidth
        justifyContent="space-between"
        alignItems="center"
        onClick={onOpen}
      >
        <Text variant="uppercaseLabel2">
          {RECORD_TYPE_LABELS[recordType]}
          {recordType === 'users' ? ` ${recordName}` : ''}
          {count ? ` (${count})` : ''}
        </Text>
        <Text variant="uppercaseLabel2" fontColor={blueGray}>
          <KeyboardArrowRightIcon />
        </Text>
      </SlimNotesCard>
    )
  }

  return (
    <ProfileHeaderCard onClick={onOpen} className="notes-card">
      <Grid columns="1fr auto">
        <Text variant="uppercaseLabel2" fontColor={darkGray}>
          <FlexBox alignItems="center" gap="10px">
            <StyledSpeakerNotesOutlinedIcon />{' '}
            <Box mt={0.5}>
              Notes{' '}
              {count && (
                <Text as="span" variant="uppercaseLabel2" fontColor={lightGray1}>
                  {count}
                </Text>
              )}
            </Box>
          </FlexBox>
        </Text>
        <Text variant="uppercaseLabel2" fontColor={blueGray}>
          <KeyboardArrowRightIcon />
        </Text>
      </Grid>
      {renderContent()}
    </ProfileHeaderCard>
  )
}

export const StyledSpeakerNotesOutlinedIcon = styled(SpeakerNotesOutlinedIcon)`
  width: 20px !important;
`

const SlimNotesCard = styled(FlexBox)`
  cursor: pointer;
  background-color: ${bgGray};
  padding: 16px 24px;
  border-radius: 6px;
`
